import React, { useState } from 'react';
import { Bell, AlertTriangle, Info, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/Popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/Tabs';
import { ScrollArea } from '../ui/ScrollArea';
import { cn } from '../../lib/utils';
type Notification = {
  id: string;
  type: 'alert' | 'info' | 'success';
  title: string;
  description: string;
  time: string;
  read: boolean;
};
const initialNotifications: Notification[] = [{
  id: '1',
  type: 'alert',
  title: 'Estoque baixo: Pastilha de Freio Dianteira',
  description: 'Apenas 8 unidades restantes — mínimo de 10',
  time: 'há 10min',
  read: false
}, {
  id: '2',
  type: 'info',
  title: 'OS-2847 aguardando aprovação',
  description: 'João Pereira ainda não respondeu o orçamento',
  time: 'há 25min',
  read: false
}, {
  id: '3',
  type: 'success',
  title: 'Pagamento recebido',
  description: 'R$ 850,00 — OS-2846 Revisão Completa',
  time: 'há 1h',
  read: false
}, {
  id: '4',
  type: 'info',
  title: 'Novo agendamento confirmado',
  description: 'Maria Oliveira — amanhã às 14h',
  time: 'há 3h',
  read: true
}, {
  id: '5',
  type: 'alert',
  title: 'Revisão pendente: Honda Civic ABC-1D23',
  description: 'Cliente não comparece há 6 meses',
  time: 'há 1 dia',
  read: true
}, {
  id: '6',
  type: 'success',
  title: 'OS-2845 finalizada',
  description: 'Entregue ao cliente — R$ 1.200,00',
  time: 'há 2 dias',
  read: true
}];
export function NotificationsPopover() {
  const [notifications, setNotifications] = useState(initialNotifications);
  const unreadCount = notifications.filter(n => !n.read).length;
  const markAsRead = (id: string) => {
    setNotifications(notifications.map(n => n.id === id ? {
      ...n,
      read: true
    } : n));
  };
  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({
      ...n,
      read: true
    })));
  };
  const NotificationIcon = ({
    type
  }: {
    type: Notification['type'];
  }) => {
    if (type === 'alert') return <AlertTriangle className="h-4 w-4 text-automotive-orange" data-id="element-193" />;
    if (type === 'success') return <CheckCircle2 className="h-4 w-4 text-emerald-500" data-id="element-194" />;
    return <Info className="h-4 w-4 text-blue-500" data-id="element-195" />;
  };
  const renderList = (items: Notification[]) => <ScrollArea className="h-[380px]" data-id="element-196">
      {items.length > 0 ? <div className="flex flex-col" data-id="element-197">
          {items.map(n => <div key={n.id} onClick={() => markAsRead(n.id)} className={cn('relative flex items-start gap-3 p-4 hover:bg-muted/50 cursor-pointer transition-colors border-b border-border last:border-0', !n.read && 'bg-primary/5')} data-id="element-198">
              <div className={cn('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border', n.type === 'alert' ? 'bg-automotive-orange/10 border-automotive-orange/20' : n.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-blue-500/10 border-blue-500/20')} data-id="element-199">
                <NotificationIcon type={n.type} data-id="element-200" />
              </div>
              <div className="flex-1 space-y-1" data-id="element-201">
                <p className="text-sm font-semibold leading-none text-foreground" data-id="element-202">
                  {n.title}
                </p>
                <p className="text-sm text-muted-foreground line-clamp-2" data-id="element-203">
                  {n.description}
                </p>
                <p className="text-xs text-muted-foreground" data-id="element-204">{n.time}</p>
              </div>
              {!n.read && <div className="absolute right-4 top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-primary" data-id="element-205" />}
            </div>)}
        </div> : <div className="flex flex-col items-center justify-center h-full p-8 text-center text-muted-foreground" data-id="element-206">
          <Bell className="h-8 w-8 mb-3 opacity-20" data-id="element-207" />
          <p className="text-sm" data-id="element-208">Nenhuma notificação por aqui.</p>
        </div>}
    </ScrollArea>;
  return <Popover data-id="element-209">
      <PopoverTrigger asChild data-id="element-210">
        <Button variant="ghost" size="icon" className="relative rounded-full w-9 h-9" data-id="element-211">
          {unreadCount > 0 && <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-automotive-orange ring-2 ring-background" data-id="element-212" />}
          <span className="sr-only" data-id="element-213">Ver notificações</span>
          <Bell className="h-5 w-5" aria-hidden="true" data-id="element-214" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="end" data-id="element-215">
        <div className="flex items-center justify-between p-4 border-b border-border" data-id="element-216">
          <h4 className="font-semibold" data-id="element-217">Notificações</h4>
          {unreadCount > 0 && <button onClick={markAllAsRead} className="text-xs font-medium text-primary hover:underline" data-id="element-218">
              Marcar todas como lidas
            </button>}
        </div>
        <Tabs defaultValue="all" className="w-full" data-id="element-219">
          <div className="px-4 pt-2 border-b border-border" data-id="element-220">
            <TabsList className="w-full justify-start h-auto p-0 bg-transparent gap-4" data-id="element-221">
              <TabsTrigger value="all" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-2 py-2" data-id="element-222">
                Tudo
              </TabsTrigger>
              <TabsTrigger value="unread" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-2 py-2" data-id="element-223">
                Não lidas {unreadCount > 0 && `(${unreadCount})`}
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="all" className="m-0" data-id="element-224">
            {renderList(notifications)}
          </TabsContent>
          <TabsContent value="unread" className="m-0" data-id="element-225">
            {renderList(notifications.filter(n => !n.read))}
          </TabsContent>
        </Tabs>
        <div className="p-2 border-t border-border text-center" data-id="element-226">
          <Button variant="ghost" className="w-full text-xs h-8" data-id="element-227">
            Ver todas as notificações
          </Button>
        </div>
      </PopoverContent>
    </Popover>;
}